import { useState, useCallback, useEffect } from 'react';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import { chatSettingsValueState, chatSettingsInputsState, useChatInteract } from '@chainlit/react-client';

interface UseModelNameReturn {
  modelName: string;
  availableModels: string[];
  isLoading: boolean;
  setModelName: (name: string) => void;
  updateModelName: (name: string) => void;
}

/**
 * Custom hook to manage model name state and connect to chat settings.
 * Automatically detects and displays the current model from chat settings.
 */
export const useModelName = (initialName = "MODEL_NAME"): UseModelNameReturn => {
  const [modelName, setModelName] = useState(initialName);
  const [hasInitialized, setHasInitialized] = useState(false);
  const chatSettingsValue = useRecoilValue(chatSettingsValueState);
  const chatSettingsInputs = useRecoilValue(chatSettingsInputsState);
  const setChatSettingsValue = useSetRecoilState(chatSettingsValueState);
  const { updateChatSettings } = useChatInteract();

  useEffect(() => {
    if (chatSettingsInputs.length > 0) {
      setHasInitialized(true);
    }
  }, [chatSettingsInputs]);

  const isLoading = !hasInitialized;
  const findModelSetting = useCallback(() => {
    const modelFieldNames = ['model', 'Model', 'MODEL', 'llm_model', 'llmModel', 'model_name', 'modelName'];
    
    for (const fieldName of modelFieldNames) {
      if (chatSettingsValue?.[fieldName]) {
        return chatSettingsValue[fieldName];
      }
    }
    
    for (const input of chatSettingsInputs) {
      if (input.label && modelFieldNames.some(name => 
        input.label.toLowerCase().includes(name.toLowerCase())
      )) {
        return chatSettingsValue?.[input.id] || input.initial || input.label;
      }
    }
    
    return null;
  }, [chatSettingsValue, chatSettingsInputs]);

  const getAvailableModels = useCallback(() => {
    const modelFieldNames = ['model', 'Model', 'MODEL', 'llm_model', 'llmModel', 'model_name', 'modelName'];
    
    for (const input of chatSettingsInputs) {
      if (input.label && modelFieldNames.some(name => 
        input.label.toLowerCase().includes(name.toLowerCase())
      )) {
        return input.items?.map((item: { label: string }) => item.label) || [];
      }
    }
    
    return [];
  }, [chatSettingsInputs]);

  useEffect(() => {
    const modelSetting = findModelSetting();
    if (modelSetting) {
      setModelName(modelSetting.toString());
    }
  }, [findModelSetting]);

  const updateModelName = useCallback((name: string) => {
    setModelName(name);
    
    const modelFieldNames = ['model', 'Model', 'MODEL', 'llm_model', 'llmModel', 'model_name', 'modelName'];
    
    for (const input of chatSettingsInputs) {
      if (input.label && modelFieldNames.some(fieldName => 
        input.label.toLowerCase().includes(fieldName.toLowerCase())
      )) {
        const selectedItem = input.items?.find((item: { label: string; value: string | number }) => item.label === name);
        if (selectedItem) {
          const updatedSettings = {
            ...chatSettingsValue,
            [input.id]: selectedItem.value
          };
          
          setChatSettingsValue(updatedSettings);
          updateChatSettings(updatedSettings);
          break;
        }
      }
    }
  }, [chatSettingsInputs, setChatSettingsValue, updateChatSettings, chatSettingsValue]);

  return {
    modelName,
    availableModels: getAvailableModels(),
    isLoading,
    setModelName,
    updateModelName
  };
};
